import { type MouseEvent, type ReactNode } from 'react'
import { RotateCcw } from 'lucide-react'
import { ContextMenu } from '@highlight/ui'
import { CellHoverButtons, POPOUT_LABEL, PopoutIcon } from '../cell-hover-buttons'
import { canPopOutView } from '../../surface'

/**
 * One move a sidepanel cell offers. A cell declares its moves once and both
 * surfaces render from that list — the floating hover cluster and the
 * right-click menu — so the two can't drift apart.
 */
export interface CellAction {
  /** Stable per move: the cluster keys its slot morph on it, React its list. */
  key: string
  /** Cluster tooltip and accessible name, and the menu item's text. */
  label: string
  icon: ReactNode
  onSelect: (event: MouseEvent) => void
  disabled?: boolean
  /** Filled cluster button — the cell's headline move (invoke, mark done, join). */
  tone?: 'brand' | 'meeting'
  /** Reads as a delete, so the menu draws it in danger styling. */
  destructive?: boolean
}

/** Amplitude `trigger` for a move fired from a cell's right-click menu — see HOVER_CLUSTER_TRIGGER. */
export const CELL_CONTEXT_MENU_TRIGGER = 'context-menu'

/** The take-back a linger window offers, worded the same for every cell that has one. */
export function undoCellAction(onSelect: () => void, options?: { disabled?: boolean }): CellAction {
  return { key: 'undo', label: 'Undo', icon: <RotateCcw />, onSelect, disabled: options?.disabled }
}

/**
 * The moves as cluster buttons. A plain function rather than a component
 * because `CellHoverButtons` keys its per-button morph off each child's own
 * key — a component in between would collapse the whole set into one slot.
 */
function cellActionButtons(actions: Array<CellAction>): Array<ReactNode> {
  return actions.map((action) =>
    action.tone ? (
      <CellHoverButtons.Primary
        key={action.key}
        tone={action.tone}
        label={action.label}
        onClick={action.onSelect}
        disabled={action.disabled}
      >
        {action.icon}
      </CellHoverButtons.Primary>
    ) : (
      <CellHoverButtons.Button
        key={action.key}
        label={action.label}
        onClick={action.onSelect}
        disabled={action.disabled}
      >
        {action.icon}
      </CellHoverButtons.Button>
    ),
  )
}

interface CellActionsClusterProps {
  /** The cell's moves, left to right — headline move last, per the design. */
  actions: Array<CellAction>
  /** Open this row's View in its own window, leading the cluster. */
  onPopout?: () => void
  /**
   * Buttons that carry subscriptions of their own, after the moves — a cell
   * whose headline move needs live status can't declare it as a descriptor.
   */
  children?: ReactNode
  className?: string
}

/** A cell's moves as its floating hover cluster. Renders nothing when it has none. */
export function CellActionsCluster({ actions, onPopout, children, className }: CellActionsClusterProps) {
  const showPopout = !!onPopout && canPopOutView
  if (!showPopout && actions.length === 0 && !children) return null

  return (
    <CellHoverButtons className={className}>
      {showPopout && <CellHoverButtons.Popout key="popout" onClick={onPopout} />}
      {cellActionButtons(actions)}
      {children}
    </CellHoverButtons>
  )
}

/** A move as a menu item, for a host composing one beside items of its own. */
export function CellActionMenuItem({ action }: { action: CellAction }) {
  return (
    <ContextMenu.Item
      variant={action.destructive ? 'danger' : undefined}
      disabled={action.disabled}
      onClick={action.onSelect}
    >
      {action.icon}
      <span>{action.label}</span>
    </ContextMenu.Item>
  )
}

interface CellActionsMenuProps {
  /** The cell's moves, in the order its cluster reads them. */
  actions?: Array<CellAction>
  /** Open this row's View in its own window. Omit on a cell with no View to open. */
  onPopout?: () => void
  /** Items with no cluster counterpart — pin, rating, draft — after the moves. */
  children?: ReactNode
}

/**
 * The right-click menu for a sidepanel cell: every move its hover cluster
 * offers, the host's own extras, then Open in new window.
 *
 * Pass it as `CellContextMenu`'s `items` so it mounts only while open.
 */
export function CellActionsMenu({ actions = [], onPopout, children }: CellActionsMenuProps) {
  const showPopout = !!onPopout && canPopOutView
  if (!showPopout && actions.length === 0 && !children) return null

  return (
    <ContextMenu.Popup>
      {actions.map((action) => (
        <CellActionMenuItem key={action.key} action={action} />
      ))}
      {children}
      {showPopout && (
        <>
          {actions.length > 0 && <ContextMenu.Separator />}
          <ContextMenu.Item onClick={onPopout}>
            <PopoutIcon />
            <span>{POPOUT_LABEL}</span>
          </ContextMenu.Item>
        </>
      )}
    </ContextMenu.Popup>
  )
}

interface CellContextMenuProps {
  /**
   * The menu — a `CellActionsMenu`. Mounted only while open, so a row that
   * defers its actions' queries and subscriptions until the pointer arrives
   * keeps deferring them until someone actually right-clicks.
   */
  items: ReactNode
  /** Open state, for a host that suppresses its hover card while the menu is up. */
  onOpenChange?: (open: boolean) => void
  /** The cell. */
  children: ReactNode
}

/** Wraps a cell in its right-click menu. */
export function CellContextMenu({ items, onOpenChange, children }: CellContextMenuProps) {
  return (
    <ContextMenu onOpenChange={onOpenChange}>
      {/* `contents`: every host's own positioning and hover group lives inside
          this trigger, so it must not become a box of its own in the layout. */}
      <ContextMenu.Trigger className="contents">{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Positioner>{items}</ContextMenu.Positioner>
      </ContextMenu.Portal>
    </ContextMenu>
  )
}
